// GalleryCarousel.tsx - Image gallery with horizontal scrolling

import React, {useState, useRef} from 'react';
import {
  View,
  Text,
  Image,
  FlatList,
  TouchableOpacity,
  Modal,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import {Colors} from '../../theme/colors';
import {GalleryImage} from '../../types/article';
import {Icon} from '../Icon';

interface GalleryCarouselProps {
  images: GalleryImage[];
}

export const GalleryCarousel: React.FC<GalleryCarouselProps> = ({images}) => {
  const {width} = useWindowDimensions();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomModalVisible, setZoomModalVisible] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);

  const imageWidth = width - 32; // Full width minus padding
  const imageHeight = (imageWidth * 4) / 3; // 3:4 aspect ratio (portrait, no crop)

  const handleImagePress = (index: number) => {
    setZoomIndex(index);
    setZoomModalVisible(true);
  };

  const handleScroll = (event: any) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    const newIndex = Math.round(offsetX / imageWidth);
    setCurrentIndex(newIndex);
  };

  const goToImage = (index: number) => {
    flatListRef.current?.scrollToIndex({index, animated: true});
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>

      {/* Image carousel */}
      <FlatList
        ref={flatListRef}
        data={images}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        keyExtractor={(_, index) => `gallery-${index}`}
        renderItem={({item, index}) => (
          <TouchableOpacity 
            activeOpacity={0.9}
            onPress={() => handleImagePress(index)}
          >
            <Image
              source={{uri: item.url}}
              style={[styles.image, {width: imageWidth, height: imageHeight, backgroundColor: '#f0f0f0'}]}
              resizeMode="contain"
            />
          </TouchableOpacity>
        )}
      />

      {/* Indicators */}
      {images.length > 1 && (
        <View style={styles.indicators}>
          {images.map((_, index) => (
            <TouchableOpacity
              key={index}
              onPress={() => goToImage(index)}
              style={[
                styles.indicator,
                index === currentIndex && styles.indicatorActive,
              ]}
            />
          ))}
        </View>
      )}

      {/* Counter */}
      <View style={styles.counter}>
        <Text style={styles.counterText}>
          {currentIndex + 1} / {images.length}
        </Text>
      </View>

      {/* Zoom Modal */}
      <Modal
        visible={zoomModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setZoomModalVisible(false)}
      >
        <View style={styles.zoomContainer}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => setZoomModalVisible(false)}
          >
            <Icon name="xmark" size={24} color="#fff" />
          </TouchableOpacity>

          <FlatList
            data={images}
            horizontal
            pagingEnabled
            initialScrollIndex={zoomIndex}
            getItemLayout={(_, index) => ({
              length: width,
              offset: width * index,
              index,
            })}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(_, index) => `zoom-${index}`}
            renderItem={({item}) => (
              <View style={[styles.zoomImageContainer, {width}]}>
                <Image
                  source={{uri: item.url}}
                  style={styles.zoomImage}
                  resizeMode="contain"
                />
                {item.description && (
                  <View style={styles.zoomCaption}>
                    <Text style={styles.zoomCaptionText}>{item.description}</Text>
                  </View>
                )}
              </View>
            )}
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.primary,
  },
  headerIcon: {
    fontSize: 16,
  },
  image: {
    borderRadius: 8,
  },
  indicators: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
    gap: 6,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ddd',
  },
  indicatorActive: {
    backgroundColor: Colors.primary,
    width: 20,
  },
  counter: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  counterText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  zoomContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  zoomImageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomImage: {
    width: '100%',
    height: '80%',
  },
  zoomCaption: {
    position: 'absolute',
    bottom: 40,
    left: 20,
    right: 20,
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: 12,
    borderRadius: 8,
  },
  zoomCaptionText: {
    color: '#fff',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default GalleryCarousel;
